import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import SubNav from "../shared/Navigation/SubNav";
import { WELCOME_SURVEY_TABS } from "./welcomeSurveyTabs";
import WelcomeSurveySummary, { ALL_OPTION } from "./WelcomeSurveySummary";
import WelcomeSurveyCustomerSubscriptions from "./WelcomeSurveyCustomerSubscriptions";
import WelcomeSurveyRepQuality from "./WelcomeSurveyRepQuality";
import "./WelcomeSurveyReportLayout.css";

// The report window defaults to the last seven days, matching the endpoint's
// own default.
function defaultDateRange() {
  return [
    dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ];
}

const DEFAULT_FILTERS = {
  officeId: ALL_OPTION,
  subscriptionId: ALL_OPTION,
  dateRange: defaultDateRange(),
};

// Dropdown sentinel -> request value: "all" means no narrowing, which the
// endpoint expects as null.
function filterValue(value) {
  return !value || value === ALL_OPTION ? null : value;
}

function filterInt(value) {
  const selected = filterValue(value);
  return selected === null ? null : Number(selected);
}

// The subscription ids in a report-data response, for the filter dropdown.
// Commented out along with the dropdown itself.
// function subscriptionIdsFrom(rows) {
//   const ids = new Set();
//   (rows ?? []).forEach(row => {
//     if (
//       row?.fr_subscription_id !== null &&
//       row?.fr_subscription_id !== undefined
//     ) {
//       ids.add(row.fr_subscription_id);
//     }
//   });
//   return [...ids].sort((a, b) => a - b);
// }

function WelcomeSurveyAdminPage() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [offices, setOffices] = useState([]);
  // const [subscriptionIds, setSubscriptionIds] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [reportDataLoading, setReportDataLoading] = useState(true);
  const [reportDataError, setReportDataError] = useState(null);
  const [viewingDocumentId, setViewingDocumentId] = useState(null);
  const [resendingSurveyId, setResendingSurveyId] = useState(null);
  const [creatingSubscriptionId, setCreatingSubscriptionId] = useState(null);
  // Bumped after a resend so the rows pick up the new sent time.
  const [refreshKey, setRefreshKey] = useState(0);

  // Resolve a completed survey's stored document to a signed link and open it.
  const handleViewDocument = useCallback(
    async documentId => {
      if (!documentId) return;
      setViewingDocumentId(documentId);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            `/v2/welcome-survey/document/${encodeURIComponent(documentId)}`
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok)
          throw new Error("Failed to load welcome survey document");

        const result = await response.json();
        const data = result?.data ?? result;
        const url = data?.url ?? data?.document_url;

        if (!url) {
          setReportDataError("No survey document was found for this survey.");
          return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setReportDataError(
          err.message || "Failed to load welcome survey document"
        );
      } finally {
        setViewingDocumentId(null);
      }
    },
    [getVerifiedJWT]
  );

  // Send an outstanding survey's link to the customer again.
  const handleResendSurvey = useCallback(
    async surveyId => {
      if (!surveyId) return;
      setResendingSurveyId(surveyId);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            `/v2/welcome-survey/resend/${encodeURIComponent(surveyId)}`
          ),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        const result = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(result?.message || "Failed to resend the survey");
        }

        const data = result?.data ?? result;
        showSuccess(
          data?.message ||
            `Survey resent by ${data?.sent_via ?? "text"}${data?.sent_to ? ` to ${data.sent_to}` : ""}`
        );
        setRefreshKey(key => key + 1);
      } catch (err) {
        showError(err.message || "Failed to resend the survey");
      } finally {
        setResendingSurveyId(null);
      }
    },
    [getVerifiedJWT, showSuccess, showError]
  );

  // Open a survey by hand for a subscription that never had one, and send the
  // customer the link.
  const handleCreateSurvey = useCallback(
    async frSubscriptionId => {
      if (!frSubscriptionId) return;
      setCreatingSubscriptionId(frSubscriptionId);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint("/v2/welcome-survey/create"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({ fr_subscription_id: frSubscriptionId }),
          }
        );

        const result = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(result?.message || "Failed to create the survey");
        }

        const data = result?.data ?? result;
        // A survey whose link couldn't be delivered is still created, and comes
        // back with sent_via null.
        showSuccess(
          data?.sent_via
            ? `Survey created and sent by ${data.sent_via}${data.sent_to ? ` to ${data.sent_to}` : ""}`
            : "Survey created, but the link could not be sent"
        );
        setRefreshKey(key => key + 1);
      } catch (err) {
        showError(err.message || "Failed to create the survey");
      } finally {
        setCreatingSubscriptionId(null);
      }
    },
    [getVerifiedJWT, showSuccess, showError]
  );

  // Offices for the filter dropdown.
  const loadOffices = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/fieldroutes/offices"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load offices");

      const result = await response.json();
      const data = result?.data ?? result;
      setOffices(Array.isArray(data) ? data : []);
    } catch {
      // A failed office list only costs the dropdown its options; the report
      // itself still loads, so this stays quiet.
      setOffices([]);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  // Rolled-up figures and customer rows for the current filters. Runs on mount
  // and on every filter change.
  useEffect(() => {
    let cancelled = false;
    const [startDate, endDate] = filters.dateRange ?? [null, null];

    // Mid-selection in the range picker — wait for the second date rather than
    // firing a request for a half-picked window.
    if (startDate && !endDate) return undefined;

    const loadReportData = async () => {
      setReportDataLoading(true);
      setReportDataError(null);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint("/v2/welcome-survey/report-data"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              office_id: filterInt(filters.officeId),
              // The endpoint's `state` narrows by the customer's US state,
              // which the report no longer offers as a filter.
              state: null,
              subscription_id: filterInt(filters.subscriptionId),
              start_date: startDate ?? null,
              end_date: endDate ?? null,
            }),
          }
        );

        if (!response.ok)
          throw new Error("Failed to load welcome survey totals");

        const result = await response.json();
        if (cancelled) return;

        const data = result?.data ?? result;
        setReportData(data);

        // Subscription options came from the response, and only refreshed while
        // no subscription was picked — otherwise picking one would narrow the
        // list down to itself.
        // if (filters.subscriptionId === ALL_OPTION) {
        //   setSubscriptionIds(subscriptionIdsFrom(data?.customer_subscriptions));
        // }
      } catch (err) {
        if (cancelled) return;
        setReportDataError(
          err.message || "Failed to load welcome survey totals"
        );
        setReportData(null);
      } finally {
        if (!cancelled) setReportDataLoading(false);
      }
    };

    loadReportData();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT, filters, refreshKey]);

  return (
    <PageContent
      customHeader={
        <SubNav
          title="Welcome Survey"
          tabs={WELCOME_SURVEY_TABS}
          activeId="report"
          ariaLabel="Welcome survey sections"
        />
      }
    >
      <div className="ws-report">
        <WelcomeSurveySummary
          data={reportData}
          loading={reportDataLoading}
          error={reportDataError}
          filters={filters}
          onFiltersChange={setFilters}
        />
        <div className="ws-report-columns">
          <WelcomeSurveyCustomerSubscriptions
            rows={reportData?.customer_subscriptions ?? []}
            loading={reportDataLoading}
            filters={filters}
            onFiltersChange={setFilters}
            offices={offices}
            onViewDocument={handleViewDocument}
            viewingDocumentId={viewingDocumentId}
            onResendSurvey={handleResendSurvey}
            resendingSurveyId={resendingSurveyId}
            onCreateSurvey={handleCreateSurvey}
            creatingSubscriptionId={creatingSubscriptionId}
          />
          {/* The endpoint's own per-rep rollup of the rows in the table beside
              it — who is behind the totals, next to the what. */}
          <WelcomeSurveyRepQuality
            reps={reportData?.rep_quality_summary ?? []}
            dateRange={filters.dateRange}
            loading={reportDataLoading}
          />
        </div>
      </div>
    </PageContent>
  );
}

export default WelcomeSurveyAdminPage;
